import { useQuery, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ServicoContaFixa = {
  servico: string;
  plano: string | null;
  valor: number;
  valor_cheio: number | null;
  desconto: number | null;
  parcela_atual: number | null;
  parcelas_total: number | null;
  fidelidade_ate: string | null;
};

export type ContaFixa = {
  id: string;
  arquivo_url: string | null;
  conta_numero: string | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  total: number | null;
  vencimento: string | null;
  titular: string | null;
  lida_em: string | null;
  solicitacao_id: string | null;
  servicos: ServicoContaFixa[];
};

/**
 * A conta FIXA (internet/TV/fixo) lida do cliente (Douglas 14/10/2026). Fica ao
 * lado da móvel, nunca no lugar dela: a proposta de renovação lê a móvel.
 */
export async function buscarContaFixa(f: { clienteId?: string | null; cnpj?: string | null; faturaId?: string | null }): Promise<ContaFixa | null> {
  let q = supabase
    .from("fatura_upload")
    .select("id, arquivo_url, conta_numero, periodo_inicio, periodo_fim, valor_atual_total, metadata, created_at")
    .eq("metadata->>tipo_conta", "FIXA")
    .is("recusada_em", null)
    .not("periodo_fim", "is", null)
    .gt("linhas_detectadas", 0);
  if (f.faturaId) q = q.eq("id", f.faturaId);
  else if (f.clienteId) q = q.eq("cliente_id", f.clienteId);
  else if (f.cnpj) q = q.eq("cnpj_fatura", f.cnpj.replace(/\D/g, ""));
  else return null;
  const { data, error } = await q.order("periodo_fim", { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  // Os serviços e a coleta saem juntos: em sequência a conta demorava a abrir na triagem (20/10/2026).
  const [{ data: itens, error: e2 }, { data: sol }] = await Promise.all([
    supabase
      .from("fatura_fixa_item")
      .select("servico, plano, valor, valor_cheio, desconto, parcela_atual, parcelas_total, fidelidade_ate")
      .eq("fatura_upload_id", data.id)
      .order("ordem", { ascending: true }),
    supabase
      .from("solicitacao_fatura")
      .select("id")
      .eq("fatura_id", data.id)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);
  if (e2) throw e2;
  const lida = ((data.metadata as { leitura_fixa?: { vencimento?: string; titular?: string; lida_em?: string } } | null)?.leitura_fixa) || {};
  return {
    id: data.id,
    arquivo_url: data.arquivo_url,
    conta_numero: data.conta_numero,
    periodo_inicio: data.periodo_inicio,
    periodo_fim: data.periodo_fim,
    total: data.valor_atual_total != null ? Number(data.valor_atual_total) : null,
    vencimento: lida.vencimento || null,
    titular: lida.titular || null,
    lida_em: lida.lida_em || null,
    solicitacao_id: sol?.id ?? null,
    servicos: ((itens || []) as ServicoContaFixa[]).map((s) => ({ ...s, valor: Number(s.valor) })),
  };
}

/** A triagem já busca a conta quando o mouse chega no valor: o clique abre pronto. */
export function preCarregarContaFixa(qc: QueryClient, faturaId: string) {
  return qc.prefetchQuery({
    queryKey: ["conta-fixa", faturaId, null, null],
    staleTime: 30_000,
    queryFn: () => buscarContaFixa({ faturaId }),
  });
}

export function useContaFixa(f: { clienteId?: string | null; cnpj?: string | null; faturaId?: string | null }, habilitado = true) {
  return useQuery({
    queryKey: ["conta-fixa", f.faturaId ?? null, f.clienteId ?? null, f.cnpj ?? null],
    enabled: habilitado && !!(f.faturaId || f.clienteId || f.cnpj),
    staleTime: 30_000,
    queryFn: () => buscarContaFixa(f),
  });
}
